import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, ListGroup, Badge, Spinner, Dropdown, Alert } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import SpeechInput from '../../components/SpeechInput';

const STARTER_PROMPTS = {
  'onboarding-delivery': {
    message: "I need help designing my customer onboarding experience. What should happen after someone buys? How do I deliver value quickly, reduce refund requests, and set customers up for success from day one?",
    category: 'implementation',
  },
  'retention-loyalty': {
    message: "I want to build a retention and loyalty strategy. How can I keep customers engaged long-term, build a community, create loyalty programs, and turn buyers into repeat customers and referral sources?",
    category: 'strategy',
  },
};

const CATEGORIES = {
  implementation: { label: 'Implementation', color: 'primary', desc: 'How do I start or execute something?' },
  strategy: { label: 'Strategy', color: 'success', desc: 'Quick strategic decisions' },
  operations: { label: 'Operations', color: 'warning', desc: 'Capacity, scaling, process challenges' },
  opportunity: { label: 'Opportunity', color: 'info', desc: 'Maximize an event or situation' },
  signals: { label: 'Market Signals', color: 'secondary', desc: 'Pattern recognition and market analysis' },
  other: { label: 'Other', color: 'dark', desc: 'General business question' }
};

function ProfitAdvisor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [speechError, setSpeechError] = useState('');
  const messagesEndRef = useRef(null);
  const messageBeforeSpeechRef = useRef('');

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Handle starter prompt from URL param
  useEffect(() => {
    const starter = searchParams.get('starter');
    if (starter && STARTER_PROMPTS[starter]) {
      setNewMessage(STARTER_PROMPTS[starter].message);
      setSelectedCategory(STARTER_PROMPTS[starter].category);
      // Clear the param from URL without navigation
      searchParams.delete('starter');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadConversations = async () => {
    try {
      const response = await fetch('/api/advisor/conversations');
      const data = await response.json();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/advisor/conversations/${conversationId}`);
      const data = await response.json();
      if (data.success) {
        setCurrentConversation(data.conversation);
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
    setLoading(false);
  };

  const startNewConversation = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await fetch('/api/advisor/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: newMessage,
          category: selectedCategory
        })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentConversation(data.conversation);
        setMessages(data.messages);
        setNewMessage('');
        loadConversations(); // Refresh sidebar
      } else {
        alert(data.error || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
      alert('Failed to start conversation');
    }
    setSending(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentConversation) return;

    const messageText = newMessage;
    setNewMessage('');
    setSending(true);

    // Optimistically add user message
    const tempUserMsg = {
      id: 'temp-user',
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await fetch(`/api/advisor/conversations/${currentConversation.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: messageText })
      });
      const data = await response.json();
      if (data.success) {
        // Replace temp message with real ones
        setMessages(prev => [
          ...prev.filter(m => m.id !== 'temp-user'),
          ...data.messages
        ]);
      } else {
        // Remove temp message on error
        setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
        alert(data.error || 'Failed to send message');
      }
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    }
    setSending(false);
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;

    try {
      const response = await fetch(`/api/advisor/conversations/${conversationId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
          setMessages([]);
        }
        loadConversations();
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const updateTitle = async (conversationId) => {
    if (!editTitleValue.trim()) {
      setEditingTitle(null);
      return;
    }

    try {
      const response = await fetch(`/api/advisor/conversations/${conversationId}/title`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitleValue })
      });
      const data = await response.json();
      if (data.success) {
        loadConversations();
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(prev => ({ ...prev, title: data.title }));
        }
      }
    } catch (error) {
      console.error('Failed to update title:', error);
    }
    setEditingTitle(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (currentConversation) {
        sendMessage();
      } else {
        startNewConversation();
      }
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <Container fluid className="py-4" style={{ height: 'calc(100vh - 120px)' }}>
      <Row className="h-100">
        {/* Sidebar - Conversation List (hidden on mobile) */}
        <Col md={3} className="h-100 d-none d-md-flex flex-column">
          <Card className="shadow-sm flex-grow-1 d-flex flex-column">
            <Card.Header className="d-flex justify-content-between align-items-center bg-white">
              <strong>Conversations</strong>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  setCurrentConversation(null);
                  setMessages([]);
                }}
                title="New Conversation"
              >
                +
              </Button>
            </Card.Header>
            <ListGroup variant="flush" className="flex-grow-1 overflow-auto">
              {conversations.length === 0 ? (
                <ListGroup.Item className="text-muted text-center py-4">
                  No conversations yet.<br />Start one below!
                </ListGroup.Item>
              ) : (
                conversations.map(conv => (
                  <ListGroup.Item
                    key={conv.id}
                    action
                    active={currentConversation?.id === conv.id}
                    onClick={() => loadConversation(conv.id)}
                    className="d-flex justify-content-between align-items-start py-2"
                  >
                    <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                      {editingTitle === conv.id ? (
                        <div className="d-flex gap-1">
                          <Form.Control
                            size="sm"
                            value={editTitleValue}
                            onChange={(e) => setEditTitleValue(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && updateTitle(conv.id)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                          <Button size="sm" variant="success" onClick={() => updateTitle(conv.id)}>
                            &#10003;
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setEditingTitle(null)}>
                            &#10005;
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="text-truncate" style={{ fontSize: '0.9rem' }}>
                            {conv.title || 'Untitled'}
                          </div>
                          <small className="text-muted d-flex align-items-center gap-2">
                            {CATEGORIES[conv.category] && (
                              <Badge bg={CATEGORIES[conv.category].color} style={{ fontSize: '0.65rem' }}>
                                {CATEGORIES[conv.category].label}
                              </Badge>
                            )}
                            <span>{formatTime(conv.updated_at)}</span>
                          </small>
                        </>
                      )}
                    </div>
                    {editingTitle !== conv.id && (
                      <Dropdown onClick={(e) => e.stopPropagation()}>
                        <Dropdown.Toggle variant="link" size="sm" className="text-muted p-0">
                          ...
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={(e) => {
                            e.stopPropagation();
                            setEditingTitle(conv.id);
                            setEditTitleValue(conv.title || '');
                          }}>
                            &#9998; Rename
                          </Dropdown.Item>
                          <Dropdown.Item className="text-danger" onClick={(e) => deleteConversation(conv.id, e)}>
                            &#128465; Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </Card>
        </Col>

        {/* Main Chat Area */}
        <Col xs={12} md={9} className="d-flex flex-column" style={{ height: 'calc(100% - 40px)', marginBottom: '40px' }}>
          <Card className="shadow-sm flex-grow-1 d-flex flex-column" style={{ minHeight: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  {/* Mobile conversation dropdown */}
                  <Dropdown className="d-md-none">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" id="mobile-conversations">
                      &#9776;
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      <Dropdown.Item onClick={() => { setCurrentConversation(null); setMessages([]); }}>
                        <strong>+ New Conversation</strong>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      {conversations.length === 0 ? (
                        <Dropdown.Item disabled>No conversations yet</Dropdown.Item>
                      ) : (
                        conversations.map(conv => (
                          <Dropdown.Item
                            key={conv.id}
                            active={currentConversation?.id === conv.id}
                            onClick={() => loadConversation(conv.id)}
                          >
                            {conv.title || 'Untitled'}
                          </Dropdown.Item>
                        ))
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                  <div>
                    <h5 className="mb-0">
                      <span role="img" aria-label="robot" className="me-2">&#129302;</span>
                      Business Strategist
                    </h5>
                    {currentConversation && (
                      <small className="text-muted">{currentConversation.title}</small>
                    )}
                  </div>
                </div>
                {!currentConversation && (
                  <Badge bg="light" text="dark" className="px-3 py-2 d-none d-sm-block">
                    Ask strategic questions about your business
                  </Badge>
                )}
              </div>
            </Card.Header>

            {/* Messages Area */}
            <Card.Body className="flex-grow-1 overflow-auto p-3" style={{ backgroundColor: '#f8f9fa' }}>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-5">
                  <span role="img" aria-label="robot" style={{fontSize: '48px'}} className="text-muted mb-3 d-block">&#129302;</span>
                  <h5 className="text-muted">Start a Strategic Conversation</h5>
                  <p className="text-muted mb-4">
                    Ask me about implementation, strategy, operations, opportunities, or market signals.
                    <br />I'll help you think through challenges using the 9-domain framework.
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <Badge
                        key={key}
                        bg={selectedCategory === key ? cat.color : 'light'}
                        text={selectedCategory === key ? 'white' : 'dark'}
                        style={{ cursor: 'pointer', padding: '8px 12px' }}
                        onClick={() => setSelectedCategory(key)}
                      >
                        {cat.label}
                      </Badge>
                    ))}
                  </div>
                  {selectedCategory && (
                    <small className="text-muted mt-2 d-block">
                      {CATEGORIES[selectedCategory]?.desc}
                    </small>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                    >
                      <div
                        className={`p-3 rounded-3 ${
                          msg.role === 'user'
                            ? 'bg-primary text-white'
                            : 'bg-white border'
                        }`}
                        style={{ maxWidth: '80%' }}
                      >
                        <div className="d-flex align-items-center mb-2">
                          {msg.role === 'user' ? (
                            <span className="me-2">&#128100;</span>
                          ) : (
                            <span className="me-2">&#129302;</span>
                          )}
                          <small className={msg.role === 'user' ? 'text-white-50' : 'text-muted'}>
                            {msg.role === 'user' ? 'You' : 'Business Strategist'}
                            {msg.created_at && ` • ${formatTime(msg.created_at)}`}
                          </small>
                        </div>
                        <div className={msg.role === 'assistant' ? 'markdown-content' : ''}>
                          {msg.role === 'assistant' ? (
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          ) : (
                            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="d-flex justify-content-start">
                      <div className="p-3 rounded-3 bg-white border">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="text-muted">Thinking...</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </Card.Body>

            {/* Input Area */}
            <Card.Footer className="bg-white border-top p-3">
              {speechError && (
                <Alert variant="warning" dismissible onClose={() => setSpeechError('')} className="mb-2 py-2">
                  {speechError}
                </Alert>
              )}
              <Form onSubmit={(e) => { e.preventDefault(); currentConversation ? sendMessage() : startNewConversation(); }}>
                <div className="d-flex gap-2">
                  {!currentConversation && (
                    <Form.Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      style={{ width: '150px' }}
                    >
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.label}</option>
                      ))}
                    </Form.Select>
                  )}
                  <div className="position-relative flex-grow-1">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={currentConversation ? "Continue the conversation..." : "What's on your mind? Ask a strategic question..."}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      style={{ resize: 'none', paddingRight: '45px' }}
                    />
                    <SpeechInput
                      onStart={() => {
                        messageBeforeSpeechRef.current = newMessage;
                        setSpeechError('');
                      }}
                      onTranscript={(text, isFinal) => {
                        const prefix = messageBeforeSpeechRef.current ? messageBeforeSpeechRef.current + ' ' : '';
                        setNewMessage(prefix + text);
                      }}
                      onError={(msg) => setSpeechError(msg)}
                      disabled={sending}
                      className="position-absolute"
                      style={{ right: '8px', top: '50%', transform: 'translateY(-50%)' }}
                      size="sm"
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={currentConversation ? sendMessage : startNewConversation}
                    disabled={sending || !newMessage.trim()}
                    style={{ minWidth: '50px' }}
                  >
                    {sending ? <Spinner animation="border" size="sm" /> : "➤"}
                  </Button>
                </div>
                <small className="text-muted mt-1 d-block">
                  Press Enter to send, Shift+Enter for new line
                </small>
              </Form>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ProfitAdvisor;
